import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../config';
import { useNavigate } from 'react-router-dom';
import logo from './css/images.png';
import Lottie from 'lottie-react';
import loader from './css/loader.json';  // Path to your loader animation JSON file

const YearSelectPage = () => {
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(''); // State for handling error
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchFinancialYears() {
      setLoading(true);
      setError(''); // Reset error state before making a new request
      try {
        const accessToken = localStorage.getItem('accessToken');
        const userId = localStorage.getItem('userId');

        const response = await fetch(`${API_BASE_URL}/api/financial-years`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
            'UserId': userId ? userId : '',
          },
        });

        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        const data = await response.json();

        if (data.length === 0) {
          throw new Error('No financial years available, check database connection');
        }

        setFinancialYears(data);
        setSelectedYear(data.length > 0 ? data[0].fyear : '');
      } catch (error) {
        console.error('Error fetching financial years:', error);
        setError(error.message || 'Failed to load financial years. Check database connection.');
      } finally {
        setLoading(false);
      }
    }

    fetchFinancialYears();
  }, []);

  const handleChange = (event) => {
    setSelectedYear(event.target.value);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (selectedYear) {
      const [fromDate, toDate] = selectedYear.split(' TO ');
      navigate(`/company-details?fromDate=${encodeURIComponent(fromDate)}&toDate=${encodeURIComponent(toDate)}`);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-4">
      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/80 backdrop-blur-sm">
          <div className="loader-container">
            <Lottie animationData={loader} loop={true} autoplay={true} style={{ height: 100, width: 100 }} />
          </div>
        </div>
      )}

      <div className="w-full max-w-[450px]">
        <div className="bg-white p-8 md:p-10 rounded-2xl shadow-xl border border-slate-100">
          <div className="flex justify-center mb-8">
            <img src={logo} alt="DeviSoft Logo" className="w-48 h-16 object-contain" />
          </div>

          <h1 className="text-2xl font-bold text-slate-800 mb-6 text-center">Select Financial Year</h1>

          {error && (
            <div className="mb-6 p-3 rounded-lg bg-red-50 border border-red-100 text-red-600 text-sm">
              {error}
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label className="block mb-2 text-sm font-semibold text-slate-700" htmlFor="year-select">
                Financial Year
              </label>
              <div className="relative">
                <select
                  className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all duration-200 appearance-none cursor-pointer"
                  id="year-select"
                  value={selectedYear}
                  onChange={handleChange}
                  disabled={loading}
                >
                  {financialYears.length === 0 ? (
                    <option value="">No financial years available</option>
                  ) : (
                    financialYears.map((year, index) => (
                      <option key={index} value={year.fyear}>
                        {year.fyear}
                      </option>
                    ))
                  )}
                </select>
                <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            <button
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white p-3.5 rounded-xl font-bold shadow-lg shadow-blue-200 transition-all duration-300 transform hover:-translate-y-0.5 active:translate-y-0"
              type="submit"
              disabled={loading || financialYears.length === 0}
            >
              {loading ? 'Loading...' : 'Continue'}
            </button>
          </form>
        </div>

        <p className="text-center mt-8 text-slate-500 text-sm">
          &copy; {new Date().getFullYear()} DeviSoft. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default YearSelectPage;
